import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { PEOPLE_CATEGORIES, PERSON_STATUSES, subsFor } from "@/constants/people";

// Add a teacher / staff member (any signed-in user). Optionally pre-linked to an institution.
export function AddPersonDialog({ institutionId = "", institutionName = "", onCreated, trigger }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    prefix: "", first_name: "", last_name: "", title: "", category: "teaching", subcategory: "",
    status: "active", start_year: "", end_year: "", department: "", subjects: "", bio: "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim() && !f.last_name.trim()) { toast.error("Please enter a name"); return; }
    setBusy(true);
    try {
      const body = {
        prefix: f.prefix, first_name: f.first_name, last_name: f.last_name, title: f.title,
        category: f.category, subcategory: f.subcategory, status: f.status, bio: f.bio,
        employments: institutionName ? [{
          institution_id: institutionId, institution_name: institutionName,
          position: f.subcategory || f.title, department: f.department,
          subjects: f.subjects ? f.subjects.split(",").map((s) => s.trim()).filter(Boolean) : [],
          start_year: f.start_year, end_year: f.end_year,
        }] : [],
      };
      const { data } = await api.post("/people", body);
      toast.success("Profile added");
      setOpen(false);
      if (onCreated) onCreated(data);
      else nav(`/person/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button size="sm" className="rounded-full gap-1.5" data-testid="add-person-open"><UserPlus size={15} /> Add a person</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="add-person-dialog">
        <DialogHeader>
          <DialogTitle>Add a teacher or staff member</DialogTitle>
          <DialogDescription>{institutionName ? <>They'll be linked to <strong>{institutionName}</strong>. </> : null}Every educator who helped build a community deserves a place in its history.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-4 gap-2">
            <div><Label className="text-xs">Prefix</Label><Input value={f.prefix} onChange={(e) => set("prefix", e.target.value)} placeholder="Mr./Dr." className="mt-1" data-testid="person-prefix" /></div>
            <div className="col-span-2"><Label className="text-xs">First name *</Label><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1" data-testid="person-first" /></div>
            <div><Label className="text-xs">Last</Label><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1" data-testid="person-last" /></div>
          </div>
          <div><Label className="text-xs">Professional title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Professor of Physics" className="mt-1" data-testid="person-title" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={f.category} onValueChange={(v) => setF((s) => ({ ...s, category: v, subcategory: "" }))}>
                <SelectTrigger className="mt-1" data-testid="person-category"><SelectValue /></SelectTrigger>
                <SelectContent>{PEOPLE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={f.subcategory} onValueChange={(v) => set("subcategory", v)}>
                <SelectTrigger className="mt-1" data-testid="person-subcategory"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent className="max-h-64">{subsFor(f.category).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {institutionName && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Department</Label><Input value={f.department} onChange={(e) => set("department", e.target.value)} className="mt-1" placeholder="e.g. Mathematics" /></div>
              <div><Label className="text-xs">Subjects (comma sep.)</Label><Input value={f.subjects} onChange={(e) => set("subjects", e.target.value)} className="mt-1" placeholder="Maths, Physics" /></div>
              <div><Label className="text-xs">From year</Label><Input value={f.start_year} onChange={(e) => set("start_year", e.target.value)} className="mt-1" placeholder="1975" data-testid="person-start" /></div>
              <div><Label className="text-xs">To year</Label><Input value={f.end_year} onChange={(e) => set("end_year", e.target.value)} className="mt-1" placeholder="1995 (blank = present)" /></div>
            </div>
          )}
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="mt-1" data-testid="person-status"><SelectValue /></SelectTrigger>
              <SelectContent>{PERSON_STATUSES.filter((s) => s.value !== "deceased").map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-muted-foreground">To mark someone as deceased, open their profile — it requires a source and creates a memorial.</p>
          </div>
          <div><Label className="text-xs">Short bio</Label><Textarea rows={3} value={f.bio} onChange={(e) => set("bio", e.target.value)} className="mt-1" placeholder="What are they remembered for?" data-testid="person-bio" /></div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full gap-1.5" data-testid="person-submit">
              {busy ? <CircleNotch size={15} className="animate-spin" /> : <UserPlus size={15} />} {busy ? "Adding…" : "Add profile"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddPersonDialog;
